import React, { useEffect, useRef, useState } from 'react';
import Box from '@nubank/nuds-web/components/Box/Box';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

import PartnerModal from '../NuBenefits/PartnerModal';

import MastercardBenefitsList from './MastercardBenefitsList';
import MastercardBenefitsGrid from './MastercardBenefitsGrid';

const StyledTypography = styled(Typography)`
  /* stylelint-disable property-no-vendor-prefix, property-no-unknown */
  background: linear-gradient(
    90deg,
    rgb(159 45 235 / 100%),
    rgb(98 0 163 / 100%)
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const StyledBox = styled(Box)`
  ::-webkit-scrollbar {
    display: none;
  }
`;

const StyledCategoryBox = styled(Box)`
  cursor: pointer;
`;

const DEFAULT_CATEGORY = 'ALL';

const filterItemsByCategoryAndDate = (partnersList, selectedCategory) => {
  const currentDate = new Date();

  return partnersList.filter(item => {
    if (item.validUntil) {
      const validUntilDate = new Date(item.validUntil);
      const isValid = validUntilDate >= currentDate;

      if (!isValid) {
        return false;
      }
    }

    if (selectedCategory && selectedCategory !== DEFAULT_CATEGORY) {
      return item.categories.some(cat => cat.id === selectedCategory);
    }

    return true;
  });
};

const MastercardBenefits = ({ partnersList, categories }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(DEFAULT_CATEGORY);
  const categoriesRef = useRef(null);

  const handleItemClick = itemId => {
    const selectedPartner = partnersList.find(item => item.id === itemId);
    setSelectedItem(selectedPartner);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'visible';
    }
  }, [isOpen]);

  const filteredItems = filterItemsByCategoryAndDate(partnersList, selectedCategory);

  const handleCategoryClick = (e, categoryId) => {
    e.preventDefault();
    const scrollToElement = document.getElementById(categoryId);
    const scrollPosition = scrollToElement.offsetLeft;
    categoriesRef.current.scrollTo({
      left: scrollPosition - 30,
      behavior: 'smooth',
    });

    setSelectedCategory(categoryId);
  };

  return (
    <Box tag="section" paddingTop="60px">
      <Typography
        intlKey="BENEFITS.PARTNERS.MASTERCARD.TITLE"
        variant="heading2"
        textAlign="center"
        intlValues={{
          highlight: (
            <StyledTypography
              tag="span"
              variant="heading2"
              intlKey="BENEFITS.PARTNERS.MASTERCARD.TITLE.HIGHLIGHT"
              textAlign="center"
              whiteSpace="pre-line"
            />
          ),
        }}
        padding={{ xs: '6x', md: '12x' }}
      />

      {/* CATEGORY MENU BAR */}

      <StyledBox
        tag="ul"
        display="flex"
        justifyContent={{ xs: 'flex-start', md: 'center' }}
        marginBottom="20px"
        marginTop="20px"
        overflow="scroll"
        width="100%"
        whiteSpace="nowrap"
        paddingLeft={{ xs: '6x', md: '0' }}
        paddingRight={{ xs: '200px', md: '0' }}
        ref={categoriesRef}
      >
        {categories.map(category => (
          <StyledCategoryBox
            key={category.id}
            id={category.id}
            tag="li"
            padding={{ xs: '8px 16px', md: '8px 20px' }}
            backgroundColor={
              selectedCategory === category.id ? 'primary.default' : 'white'
            }
            borderRadius={selectedCategory === category.id ? '100px' : '0'}
            onClick={e => handleCategoryClick(e, category.id)}
          >
            <Typography
              variant="subtitle1"
              color={selectedCategory === category.id ? 'white' : 'black'}
              strong
              whiteSpace="nowrap"
            >
              {category.name}
            </Typography>
          </StyledCategoryBox>
        ))}
      </StyledBox>

      {/* MOBILE LIST */}

      <Box display={{ xs: 'block', md: 'none' }}>
        <MastercardBenefitsList
          partnersList={filteredItems}
          handleItemClick={handleItemClick}
        />
      </Box>

      {/* DESKTOP GRID */}

      <Box
        display={{ xs: 'none', md: 'block' }}
        background="linear-gradient(to bottom,white 0%,white 15%,#F8F8FA 15%,#F8F8FA 100%)"
      >
        <MastercardBenefitsGrid
          partnersList={filteredItems}
          handleItemClick={handleItemClick}
        />
      </Box>

      {/* CLOSING TEXT */}

      <Box
        backgroundColor="#F8F8FA"
        paddingBottom="70px"
        paddingTop="50px"
      >
        <Typography
          variant="subtitle1"
          intlKey="BENEFITS.PARTNERS.MASTERCARD.DESCRIPTION_BANK"
          width={{ xs: '303px', md: '503px' }}
          margin="0 auto"
          strong
          textAlign="center"
        />
      </Box>

      {isOpen
        && selectedItem
        && createPortal(
          <PartnerModal closeModal={closeModal} item={selectedItem} />,
          document.getElementById('modal-root'),
        )}
    </Box>
  );
};

MastercardBenefits.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  partnersList: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      id: PropTypes.string,
      image: PropTypes.shape({
        alt: PropTypes.string,
        url: PropTypes.string,
      }),
      moreLink: PropTypes.string,
      title: PropTypes.string,
      validUntil: PropTypes.string,
    }),
  ).isRequired,
};

export default MastercardBenefits;
